import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatApiErrorDetail } from '@/lib/api';
import { ArrowUpRight } from 'lucide-react';

export default function AdminLogin() {
  const { user, checked, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (checked && user && user.role === 'admin') nav('/admin', { replace: true });
  }, [checked, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back.');
      nav('/admin', { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen bg-paper flex flex-col">
      <div className="p-6 md:p-10 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-ink-dim">
        <div className="flex items-center gap-3">
          <span className="dot" />
          <span>Rotama · Studio access</span>
        </div>
        <a href="/" className="link-ember">← Back to site</a>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-paper-2 border border-[var(--line)] p-8 md:p-12 clip-frame"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-ember mb-4">
            § Restricted
          </div>
          <h1 className="font-serif text-4xl md:text-5xl leading-none mb-10">
            Admin
            <span className="italic text-ember">.</span>
          </h1>
          <div className="mb-6">
            <label className="block font-mono text-[10px] uppercase tracking-[0.28em] text-ink-dim mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full bg-transparent border-b border-[var(--line-strong)] focus:border-ember focus:outline-none py-3 text-ink font-serif"
              required
            />
          </div>
          <div className="mb-10">
            <label className="block font-mono text-[10px] uppercase tracking-[0.28em] text-ink-dim mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full bg-transparent border-b border-[var(--line-strong)] focus:border-ember focus:outline-none py-3 text-ink font-serif"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-ember disabled:opacity-60 w-full justify-center"
          >
            {loading ? '…signing in' : 'Sign in'}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </form>
      </div>
      <div className="p-6 md:p-10 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-dim text-center">
        Rotama Company Ltd — Editorial CMS · v1
      </div>
    </div>
  );
}
